import { useState } from "react";
import {
  Timestamp,
  uuid,
  type PackingItem,
  type Trip,
} from "@travelix/core";
import { ListChecks, X } from "lucide-react";

import { useAuth } from "@/lib/auth-provider";
import { EmptyState } from "@/components/empty-state";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetFooter } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  addPackingItem,
  claimPackingItem,
  removePackingItem,
  unclaimPackingItem,
  unwrap,
  queryKeys,
  displayNameOf,
} from "@/lib/api";
import { useTripMutation } from "@/hooks/use-trip-mutation";

/**
 * Packing — collaborative bring-list per trip. Anyone can add items;
 * anyone can claim ("I'll bring the speaker"). Claim/release lives
 * per-row; adding lives in a bottom sheet triggered by PlanTab's
 * bottom action bar.
 *
 * Sorted so unclaimed items float to the top — that's what the group
 * needs to see first.
 */
export function PackingSection({
  trip,
  adding,
  onCloseAdd,
  readOnly = false,
}: {
  trip: Trip;
  adding: boolean;
  onCloseAdd: () => void;
  /** Past trips: no add, no claim/unclaim, no remove. */
  readOnly?: boolean;
}) {
  const { user } = useAuth();

  const items = trip.packingItems ?? [];
  const currentUid = user?.uid ?? null;

  // Unclaimed first (top of mind), then mine, then others'
  const sorted = [...items].sort((a, b) => {
    const rank = (i: PackingItem) => {
      if (!i.claimedBy) return 0;
      if (i.claimedBy === currentUid) return 1;
      return 2;
    };
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    return a.addedAt.toMillis() - b.addedAt.toMillis();
  });

  const claim = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: async (itemId: string) => {
      if (!user?.uid) throw new Error("Not signed in.");
      const name = displayNameOf(user);
      await unwrap(claimPackingItem(trip.id, itemId, user.uid, name));
    },
    errorToast: "Couldn't claim.",
  });

  const unclaim = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: (itemId: string) => unwrap(unclaimPackingItem(trip.id, itemId)),
    errorToast: "Couldn't release.",
  });

  const remove = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: (itemId: string) => unwrap(removePackingItem(trip.id, itemId)),
    errorToast: "Couldn't remove.",
  });

  // One-tap remove sat right next to other row actions — confirm first
  // so a stray tap doesn't drop something the group was counting on.
  const [pendingRemove, setPendingRemove] = useState<PackingItem | null>(null);

  const unclaimedCount = items.filter((i) => !i.claimedBy).length;
  const mineCount = items.filter((i) => i.claimedBy === currentUid).length;

  return (
    <>
      {items.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title={readOnly ? "Nothing was on the list" : "Nothing to bring yet"}
          body={
            readOnly
              ? "This trip is in the past — nothing more can be added."
              : "Add what the group needs to remember — someone can claim each item so you don't end up with three chargers and no adapter."
          }
        />
      ) : (
        <>
          <div className="flex gap-2 text-[12px] text-ink-muted">
            <span>{unclaimedCount} unclaimed</span>
            <span aria-hidden>·</span>
            <span>{mineCount} on you</span>
          </div>

          <ul className="flex flex-col divide-y divide-line rounded-xl border border-line bg-bg-elevated">
            {sorted.map((it) => (
              <PackingRow
                key={it.id}
                item={it}
                mine={it.claimedBy === currentUid}
                onClaim={() => claim.mutate(it.id)}
                onUnclaim={() => unclaim.mutate(it.id)}
                onRemove={() => setPendingRemove(it)}
                canRemove={it.addedBy === currentUid && !it.claimedBy && !readOnly}
                busy={claim.isPending || unclaim.isPending || remove.isPending}
                lockedForReadOnly={readOnly}
              />
            ))}
          </ul>
        </>
      )}

      {adding && !readOnly ? <AddPackingSheet trip={trip} onClose={onCloseAdd} /> : null}

      <ConfirmDialog
        open={!!pendingRemove}
        onOpenChange={(v) => !v && setPendingRemove(null)}
        title={`Remove "${pendingRemove?.name ?? ""}"?`}
        description="It disappears from the packing list for everyone."
        confirmLabel="Remove"
        destructive
        onConfirm={async () => {
          if (pendingRemove) await remove.mutateAsync(pendingRemove.id);
          setPendingRemove(null);
        }}
      />
    </>
  );
}

// ─── Add-item sheet ─────────────────────────────────────────
function AddPackingSheet({ trip, onClose }: { trip: Trip; onClose: () => void }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const add = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: async () => {
      const t = text.trim();
      if (!t) throw new Error("Add an item.");
      if (!user?.uid) throw new Error("Not signed in.");
      const item: PackingItem = {
        id: uuid(),
        name: t,
        addedBy: user.uid,
        addedByName: displayNameOf(user),
        addedAt: Timestamp.now(),
      };
      await unwrap(addPackingItem(trip.id, item));
    },
    successToast: "Added to Packing.",
    onError: (e) => setErr(e instanceof Error ? e.message : "Couldn't add."),
    onSuccess: onClose,
  });

  return (
    <Sheet open guardUnsaved onOpenChange={(o) => !o && !add.isPending && onClose()}>
      <SheetContent>
        <SheetHeader
          title="New packing item"
          description="What does the group need to bring? Any member can claim it after."
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <SheetBody>
            <Field id="new-pack" label="What to bring" error={err ?? undefined}>
              <Input
                id="new-pack"
                type="text"
                autoFocus
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  if (err) setErr(null);
                }}
                placeholder="Speaker, sunscreen, HDMI cable…"
                maxLength={80}
              />
            </Field>
          </SheetBody>
          <SheetFooter>
            <Button type="submit" block disabled={add.isPending || !text.trim()}>
              {add.isPending ? "Adding…" : "Add to Packing"}
            </Button>
            <Button type="button" variant="ghost" block onClick={onClose} disabled={add.isPending}>
              Cancel
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

// ─── Row ────────────────────────────────────────────────────
function PackingRow({
  item,
  mine,
  onClaim,
  onUnclaim,
  onRemove,
  canRemove,
  busy,
  lockedForReadOnly = false,
}: {
  item: PackingItem;
  mine: boolean;
  onClaim: () => void;
  onUnclaim: () => void;
  onRemove: () => void;
  canRemove: boolean;
  busy: boolean;
  /** True on past trips — no claim/unclaim/remove exposed. */
  lockedForReadOnly?: boolean;
}) {
  const claimed = !!item.claimedBy;

  return (
    <li
      className={cn(
        "flex items-center gap-3 px-4 py-3",
        mine && "bg-primary-soft/25",
      )}
    >
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "text-[15px] font-medium text-ink m-0 truncate",
            claimed && !mine && "text-ink-muted",
          )}
        >
          {item.name}
        </p>
        <p className="text-[12px] text-ink-subtle m-0 truncate">
          Added by {item.addedByName}
        </p>
      </div>

      {claimed ? (
        <div className="flex items-center gap-2">
          <Avatar
            name={item.claimedByName ?? "?"}
            colorSeed={hashSeed(item.claimedBy!)}
            size="sm"
          />
          <span
            className={cn(
              "text-[12px] font-medium",
              mine ? "text-primary-ink" : "text-ink-muted",
            )}
          >
            {mine ? "You" : item.claimedByName}
          </span>
          {mine && !lockedForReadOnly ? (
            <button
              type="button"
              onClick={onUnclaim}
              disabled={busy}
              aria-label="Release claim"
              className="flex h-7 w-7 items-center justify-center rounded-full text-ink-subtle hover:bg-bg-sunken hover:text-ink disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ) : lockedForReadOnly ? (
        <span className="text-[12px] font-medium text-ink-subtle">
          Unclaimed
        </span>
      ) : (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClaim}
            disabled={busy}
            className="text-primary hover:bg-primary-soft"
          >
            Claim
          </Button>
          {canRemove ? (
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              aria-label="Remove item"
              className="flex h-7 w-7 items-center justify-center rounded-full text-ink-subtle hover:bg-danger-soft/40 hover:text-danger disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </>
      )}
    </li>
  );
}

function hashSeed(uid: string): number {
  let h = 0;
  for (let i = 0; i < uid.length; i++) h = (h * 31 + uid.charCodeAt(i)) | 0;
  return Math.abs(h);
}
